import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';

type ConnectionStatus = 'connected' | 'disconnected' | 'reconnecting';

interface BufferedMessage {
  orderingKey: string;
  sequence: number;
  data: any;
  bufferedAt: number;
}

interface WebSocketState {
  connectionStatus: ConnectionStatus;
  reconnectCountdown: number;
  sequenceNumbers: { [orderingKey: string]: number };
  bufferedMessages: BufferedMessage[];
  lastMessageTimestamp: number;
}

const initialState: WebSocketState = {
  connectionStatus: 'disconnected',
  reconnectCountdown: 0,
  sequenceNumbers: {},
  bufferedMessages: [],
  lastMessageTimestamp: 0
};

const websocketSlice = createSlice({
  name: 'websocket',
  initialState,
  reducers: {
    // Connection status
    setConnectionStatus: (state, action: PayloadAction<ConnectionStatus>) => {
      state.connectionStatus = action.payload;
    },

    // Reconnect countdown
    setReconnectCountdown: (state, action: PayloadAction<number>) => {
      state.reconnectCountdown = action.payload;
    },

    // Sequence number management
    setSequenceNumber: (state, action: PayloadAction<{ orderingKey: string; sequence: number }>) => {
      const { orderingKey, sequence } = action.payload;
      state.sequenceNumbers[orderingKey] = sequence;
    },

    incrementSequence: (state, action: PayloadAction<string>) => {
      const orderingKey = action.payload;
      const current = state.sequenceNumbers[orderingKey] || 1;
      state.sequenceNumbers[orderingKey] = current + 1;
    },

    // Message buffering
    bufferMessage: (state, action: PayloadAction<BufferedMessage>) => {
      state.bufferedMessages.push(action.payload);
    },

    removeBufferedMessages: (state, action: PayloadAction<string[]>) => {
      const idsToRemove = action.payload;
      state.bufferedMessages = state.bufferedMessages.filter(
        msg => !idsToRemove.includes(`${msg.orderingKey}-${msg.sequence}`)
      );
    },

    clearBufferedMessages: (state, action: PayloadAction<string>) => {
      const orderingKey = action.payload;
      state.bufferedMessages = state.bufferedMessages.filter(
        msg => msg.orderingKey !== orderingKey
      );
    },

    // Force process buffered messages (timeout scenario)
    forceProcessBuffered: (state, action: PayloadAction<string>) => {
      const orderingKey = action.payload;
      const buffered = state.bufferedMessages.filter(msg => msg.orderingKey === orderingKey);

      if (buffered.length > 0) {
        // Sort and set sequence to the minimum buffered sequence
        buffered.sort((a, b) => a.sequence - b.sequence);
        state.sequenceNumbers[orderingKey] = buffered[0].sequence;
      }
    },

    // Last message timestamp (for monitoring)
    updateLastMessageTimestamp: (state) => {
      state.lastMessageTimestamp = Date.now();
    },

    // Clear all WebSocket state
    clearWebSocketState: (state) => {
      state.connectionStatus = 'disconnected';
      state.reconnectCountdown = 0;
      state.sequenceNumbers = {};
      state.bufferedMessages = [];
      state.lastMessageTimestamp = 0;
    }
  }
});

export const {
  setConnectionStatus,
  setReconnectCountdown,
  setSequenceNumber,
  incrementSequence,
  bufferMessage,
  removeBufferedMessages,
  clearBufferedMessages,
  forceProcessBuffered,
  updateLastMessageTimestamp,
  clearWebSocketState
} = websocketSlice.actions;

// Selectors
export const selectConnectionStatus = (state: RootState) => state.websocket.connectionStatus;
export const selectIsConnected = (state: RootState) => state.websocket.connectionStatus === 'connected';
export const selectReconnectCountdown = (state: RootState) => state.websocket.reconnectCountdown;
export const selectSequenceNumber = (state: RootState, orderingKey: string) =>
  state.websocket.sequenceNumbers[orderingKey] || 1;
export const selectBufferedMessages = (state: RootState, orderingKey?: string) =>
  orderingKey
    ? state.websocket.bufferedMessages.filter(msg => msg.orderingKey === orderingKey)
    : state.websocket.bufferedMessages;
export const selectLastMessageTimestamp = (state: RootState) => state.websocket.lastMessageTimestamp;

export default websocketSlice.reducer;
